import type {
  BoardData,
  BuiltinLayerKey,
  Item,
  ItemKind,
  Layer,
  LayerKind,
  Meta,
} from './schema.js';
import { BUILTIN_LAYER_KEYS } from './schema.js';

/** Layers visible when a new board opens (decision R9/S3). Stage + notes are base layers, also visible. */
export const DEFAULT_VISIBLE_LAYERS: readonly BuiltinLayerKey[] = [
  'stage',
  'risers',
  'musicians',
  'backline',
  'power',
  'microphones',
  'stands',
  'monitors',
  'notes',
  'ink',
];

export const LAYER_NAMES: Record<BuiltinLayerKey, string> = {
  stage: 'Stage',
  risers: 'Risers',
  musicians: 'Musicians',
  backline: 'Backline / Instruments',
  microphones: 'Microphones',
  stands: 'Stands',
  monitors: 'Monitors',
  pa: 'PA & Amps',
  stageboxes: 'Stage boxes',
  subcores: 'Sub-cores',
  cabling: 'Audio cabling',
  power: 'Power',
  network: 'Network',
  lighting: 'Lighting',
  notes: 'Notes',
  ink: 'Ink',
};

export interface LayerPreset {
  id: 'foh' | 'monitors' | 'power' | 'backline';
  name: string;
  layers: readonly BuiltinLayerKey[];
}

export const LAYER_PRESETS: readonly LayerPreset[] = [
  {
    id: 'foh',
    name: 'FOH',
    layers: [
      'stage',
      'risers',
      'musicians',
      'backline',
      'microphones',
      'stands',
      'stageboxes',
      'subcores',
      'pa',
      'notes',
      'ink',
    ],
  },
  {
    id: 'monitors',
    name: 'Monitors',
    layers: [
      'stage',
      'risers',
      'musicians',
      'microphones',
      'stands',
      'monitors',
      'stageboxes',
      'subcores',
      'notes',
      'ink',
    ],
  },
  {
    id: 'power',
    name: 'Power',
    layers: ['stage', 'risers', 'musicians', 'backline', 'power', 'notes', 'ink'],
  },
  {
    id: 'backline',
    name: 'Backline',
    layers: ['stage', 'risers', 'musicians', 'backline', 'notes', 'ink'],
  },
];

const KIND_LAYER: Record<ItemKind, BuiltinLayerKey> = {
  musician: 'musicians',
  instrument: 'backline',
  microphone: 'microphones',
  di: 'microphones',
  stand: 'stands',
  riser: 'risers',
  wedge: 'monitors',
  sidefill: 'monitors',
  iem: 'monitors',
  monitor_mixer: 'monitors',
  speaker: 'pa',
  sub: 'pa',
  amp: 'pa',
  stagebox: 'stageboxes',
  subcore: 'subcores',
  console: 'stageboxes',
  power_distro: 'power',
  network_switch: 'network',
  light: 'lighting',
  truss: 'lighting',
  generic: 'backline',
};

/** The built-in layer an item of this kind lands on by default. */
export function defaultLayerKeyFor(kind: ItemKind): BuiltinLayerKey {
  return KIND_LAYER[kind];
}

/** Stable id for built-in layers so documents, presets and link restrictions can refer to them. */
export function builtinLayerId(key: BuiltinLayerKey): string {
  return `layer:${key}`;
}

/**
 * Changing an item's kind: an item still on its old kind's default layer moves to the new kind's
 * (when the board has it) – a mic retyped from an instrument no longer stays on Backline and
 * survives hiding the Microphones layer (exploratory test OIL-127). Hand-picked layers stay.
 */
export function kindChange(
  item: Pick<Item, 'kind' | 'layerId'>,
  kind: ItemKind,
  layers: Readonly<Record<string, unknown>>,
): { kind: ItemKind; layerId?: string } {
  const from = builtinLayerId(defaultLayerKeyFor(item.kind));
  const to = builtinLayerId(defaultLayerKeyFor(kind));
  return item.layerId === from && to !== from && layers[to] !== undefined
    ? { kind, layerId: to }
    : { kind };
}

/** Built-in layers holding ink instead of objects (decision I2: "Ink", topmost). */
const INK_LAYER_KEYS: readonly BuiltinLayerKey[] = ['ink'];

/** Id of the built-in ink layer, where strokes go unless another ink layer is active. */
export const INK_LAYER_ID = builtinLayerId('ink');

export function createBuiltinLayers(): Layer[] {
  return BUILTIN_LAYER_KEYS.map((key, order) => {
    const layer: Layer = {
      id: builtinLayerId(key),
      key,
      name: LAYER_NAMES[key],
      visible: DEFAULT_VISIBLE_LAYERS.includes(key),
      locked: false,
      order,
    };
    if (INK_LAYER_KEYS.includes(key)) layer.kind = 'ink';
    return layer;
  });
}

/** The built-in ink layer (topmost when `order` is above every other layer). */
export function createInkLayer(order: number): Layer {
  return {
    id: INK_LAYER_ID,
    key: 'ink',
    name: LAYER_NAMES.ink,
    visible: true,
    locked: false,
    order,
    kind: 'ink',
  };
}

/** Kind of a layer; layers without `kind` hold objects. */
export function layerKind(layer: Pick<Layer, 'kind'>): LayerKind {
  return layer.kind ?? 'objects';
}

export function isInkLayer(layer: Pick<Layer, 'kind'> | undefined): boolean {
  return layer !== undefined && layerKind(layer) === 'ink';
}

/**
 * Visibility map after applying a preset: every layer with a key in the preset is visible, others
 * hidden. Custom layers and layers hidden for the board (U13) keep their state.
 */
export function applyPreset(layers: readonly Layer[], preset: LayerPreset): Layer[] {
  return layers.map((l) =>
    l.key === undefined || isHiddenLayer(l) ? l : { ...l, visible: preset.layers.includes(l.key) },
  );
}

/** Hidden for the whole board by an owner/admin (decision U13). */
export function isHiddenLayer(layer: Pick<Layer, 'hiddenInPanel'>): boolean {
  return layer.hiddenInPanel === true;
}

/** "Hide empty layers" of a board (decision U13), on unless switched off. */
export function hideEmptyLayersOn(meta: Pick<Meta, 'hideEmptyLayers'>): boolean {
  return meta.hideEmptyLayers !== false;
}

/** Ids of layers holding at least one item, free-form element or ink stroke. */
export function layersWithContent(
  data: Pick<BoardData, 'items' | 'freeform' | 'ink'>,
): Set<string> {
  const out = new Set<string>();
  for (const i of Object.values(data.items)) out.add(i.layerId);
  for (const el of Object.values(data.freeform)) if (el.layerId !== undefined) out.add(el.layerId);
  for (const s of Object.values(data.ink)) out.add(s.layerId);
  return out;
}

/**
 * Layers listed in the layers panel (decision U13), in the given order: never the hidden ones;
 * with "hide empty layers" on, built-in object layers only once something lies on them. The
 * stage layer (the frame), ink layers and the user's own layers are always listed.
 */
export function panelLayers(
  data: Pick<BoardData, 'meta' | 'items' | 'freeform' | 'ink'>,
  layers: readonly Layer[],
): Layer[] {
  const hideEmpty = hideEmptyLayersOn(data.meta);
  const filled = hideEmpty ? layersWithContent(data) : new Set<string>();
  return layers.filter(
    (l) =>
      !isHiddenLayer(l) &&
      (!hideEmpty || l.key === undefined || l.key === 'stage' || isInkLayer(l) || filled.has(l.id)),
  );
}

/** Layers hidden for the board, in the given order ("Hidden layers…" menu). */
export function hiddenLayers(layers: readonly Layer[]): Layer[] {
  return layers.filter(isHiddenLayer);
}
